import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth'
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore'
import { auth, db } from '../../plugins/firebase'

// Async thunks
export const handleLogin = createAsyncThunk(
  'user/handleLogin',
  async (useTestAccount, { dispatch, rejectWithValue }) => {
    try {
      // Ensure persistence is set to LOCAL before signing in
      await setPersistence(auth, browserLocalPersistence)

      let userDoc, authResponse

      if (useTestAccount) {
        const testEmail = import.meta.env.VITE_TEST_USER_EMAIL
        const testPassword = import.meta.env.VITE_TEST_USER_PASSWORD

        if (!testEmail || !testPassword) {
          throw new Error('Test user credentials not configured')
        }

        authResponse = await signInWithEmailAndPassword(auth, testEmail, testPassword)
      } else {
        const provider = new GoogleAuthProvider()
        provider.addScope('profile')
        provider.addScope('email')

        console.log('Attempting Google sign-in...')
        authResponse = await signInWithPopup(auth, provider)
        console.log('Google sign-in successful:', authResponse.user)
      }

      const userRef = doc(db, 'users', authResponse.user.uid)
      userDoc = await getDoc(userRef)

      if (!userDoc.exists()) {
        await setDoc(userRef, {
          displayName: authResponse.user.displayName || 'Test User',
          photoURL: authResponse.user.photoURL,
          email: authResponse.user.email,
          uid: authResponse.user.uid,
          enableAutoSave: false,
          enableDarkMode: false
        })
      }

      // Always pull fresh doc after create
      userDoc = await getDoc(userRef)
      return userDoc.data()
    } catch (error) {
      console.error('Login error:', error)
      return rejectWithValue(error.message)
    }
  }
)

export const handleLogout = createAsyncThunk(
  'user/handleLogout',
  async () => {
    await signOut(auth)
  }
)

export const nukeUserAccount = createAsyncThunk(
  'user/nukeUserAccount',
  async (_, { getState }) => {
    const { user } = getState()
    await deleteDoc(doc(db, 'users', user.userInfo.uid))
  }
)

export const repairUserAccount = createAsyncThunk(
  'user/repairUserAccount',
  async (_, { getState }) => {
    const { user } = getState()
    const userDoc = await getDoc(doc(db, 'users', user.userInfo.uid))
    
    const userInfoKeysToTrack = [
      'displayName',
      'photoURL',
      'email',
      'enableAutoSave',
      'enableDarkMode'
    ]
    
    userInfoKeysToTrack.every((key) => key in userDoc.data())
    
    // Simulate repair time
    await new Promise((resolve) => setTimeout(resolve, 3000))
    return true
  }
)

const userSlice = createSlice({
  name: 'user',
  initialState: {
    userIsAuthenticated: false,
    accountMenu: false,
    userInfo: null,
    hasProfileBeenRepaired: false,
    loading: false,
    error: null,
  },
  reducers: {
    setAccountMenu: (state, action) => {
      state.accountMenu = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(handleLogin.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(handleLogin.fulfilled, (state, action) => {
        state.loading = false
        state.userIsAuthenticated = true
        state.userInfo = action.payload
      })
      .addCase(handleLogin.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
      .addCase(handleLogout.fulfilled, (state) => {
        state.userIsAuthenticated = false
        state.userInfo = null
        state.accountMenu = false
      })
      .addCase(nukeUserAccount.fulfilled, (state) => {
        state.userInfo = null
        state.accountMenu = false
      })
      .addCase(repairUserAccount.fulfilled, (state) => {
        state.hasProfileBeenRepaired = true
      })
  },
})

export const { setAccountMenu, clearError } = userSlice.actions

// Selectors
export const selectUserIsAuthenticated = (state) => state.user.userIsAuthenticated
export const selectUserInfo = (state) => state.user.userInfo
export const selectAccountMenu = (state) => state.user.accountMenu
export const selectUserDisplayName = (state) => state.user.userInfo?.displayName
export const selectUserPhotoURL = (state) => 
  state.user.userInfo?.photoURL || 'https://randomuser.me/api/portraits/lego/1.jpg'
export const selectUserUid = (state) => state.user.userInfo?.uid
export const selectUserEmail = (state) => state.user.userInfo?.email

export default userSlice.reducer